// src/components/loadletter/templateFields.ts
//
// Personalised fields for load letter templates, and the conversion between the
// stored template HTML and what the visual editor works with.
//
// Templates are STORED exactly as before: HTML with {{field}} placeholders, which
// templateRenderer replaces when letters are generated. The editor shows each
// placeholder as a labelled tag (a `<span data-template-field>` atom), so the
// conversion runs in both directions and the stored format never changes.

import DOMPurify from 'dompurify';

export interface TemplateField {
  key: string;
  label: string;
  group: string;
}

// Every placeholder templateRenderer.replaceVariables substitutes.
export const TEMPLATE_FIELDS: TemplateField[] = [
  { key: 'staffName', label: 'Staff name', group: 'Staff' },
  { key: 'staffCode', label: 'Staff code', group: 'Staff' },
  { key: 'department', label: 'Department', group: 'Staff' },
  { key: 'curriculumArea', label: 'Learning area', group: 'Staff' },
  { key: 'academicYear', label: 'Academic year', group: 'Letter' },
  { key: 'priorYear', label: 'Previous year', group: 'Letter' },
  { key: 'semester', label: 'Semester', group: 'Letter' },
  { key: 'issueDate', label: 'Issue date', group: 'Letter' },
  { key: 'dueDate', label: 'Response due date', group: 'Letter' },
  { key: 'fte', label: 'FTE', group: 'Load' },
  { key: 'targetMinutes', label: 'Target minutes', group: 'Load' },
  { key: 'allocatedMinutes', label: 'Allocated minutes', group: 'Load' },
  { key: 'teachingMinutes', label: 'Teaching minutes', group: 'Load' },
  { key: 'allowanceMinutes', label: 'Allowance minutes', group: 'Load' },
  { key: 'timetabledSubs', label: 'Timetabled subs', group: 'Load' },
  { key: 'utilization', label: 'Utilisation', group: 'Load' },
  { key: 'polPosition', label: 'POL position', group: 'Load' },
  { key: 'polMinutes', label: 'POL minutes', group: 'Load' },
  { key: 'otherAllowance', label: 'Other allowance minutes', group: 'Load' },
  { key: 'collegeName', label: 'College name', group: 'College' },
  { key: 'collegeAddress', label: 'College address', group: 'College' },
  { key: 'collegePhone', label: 'College phone', group: 'College' },
  { key: 'collegeEmail', label: 'College email', group: 'College' },
  { key: 'collegeWebsite', label: 'College website', group: 'College' },
  { key: 'collegeTagline', label: 'College tagline', group: 'College' },
  { key: 'principalName', label: 'Principal name', group: 'College' },
  { key: 'meaGuidelinesUrl', label: 'MEA guidelines link', group: 'College' },
];

// Section-specific placeholders, handled by templateRenderer.renderLetterContent
// rather than replaceVariables, so they only make sense in one section each.
export const MEETING_SCHEDULE_FIELD: TemplateField = {
  key: 'meetingSchedule', label: 'Meeting schedule section', group: 'Sections',
};
export const SIGNATURE_FIELD: TemplateField = {
  key: 'principalSignature', label: "Principal's signature image", group: 'Sections',
};

const ALL_FIELDS = [...TEMPLATE_FIELDS, MEETING_SCHEDULE_FIELD, SIGNATURE_FIELD];

export const fieldLabel = (key: string): string =>
  ALL_FIELDS.find(f => f.key === key)?.label ?? key;

export const isKnownField = (key: string): boolean => ALL_FIELDS.some(f => f.key === key);

export const FIELD_ATTR = 'data-template-field';

const PLACEHOLDER = /\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g;
const BULLET = /^\s*(?:[-*•–]|\d+[.)])\s+/;

const parse = (html: string): HTMLElement => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return doc.body;
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * A section typed as plain text (no tags at all) renders as one run-on block,
 * which is what prompted the visual editor. Turn its lines into paragraphs, and
 * runs of "- item" / "• item" / "1. item" lines into real lists.
 */
const plainTextToHtml = (text: string): string => {
  const out: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const tag = list.ordered ? 'ol' : 'ul';
    out.push(`<${tag}>${list.items.map(i => `<li>${i}</li>`).join('')}</${tag}>`);
    list = null;
  };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (BULLET.test(line)) {
      const ordered = /^\s*\d/.test(line);
      if (list && list.ordered !== ordered) flush();
      if (!list) list = { ordered, items: [] };
      list.items.push(escapeHtml(line.replace(BULLET, '')));
    } else {
      flush();
      out.push(`<p>${escapeHtml(line)}</p>`);
    }
  }
  flush();
  return out.join('');
};

/** Stored template HTML → HTML the editor loads (placeholders become field tags). */
export const toEditorHtml = (stored: string): string => {
  if (!stored.trim()) return '';
  const html = /<[a-z][\s\S]*>/i.test(stored) ? stored : plainTextToHtml(stored);
  const body = parse(html);

  // Only text nodes: a placeholder inside an attribute (href="{{meaGuidelinesUrl}}")
  // must stay as it is.
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  for (const node of textNodes) {
    const text = node.nodeValue ?? '';
    PLACEHOLDER.lastIndex = 0;
    if (!PLACEHOLDER.test(text)) continue;
    const frag = body.ownerDocument.createDocumentFragment();
    let last = 0;
    text.replace(PLACEHOLDER, (match, key: string, offset: number) => {
      if (offset > last) frag.appendChild(body.ownerDocument.createTextNode(text.slice(last, offset)));
      const span = body.ownerDocument.createElement('span');
      span.setAttribute(FIELD_ATTR, key);
      span.textContent = `{{${key}}}`;
      frag.appendChild(span);
      last = offset + match.length;
      return match;
    });
    if (last < text.length) frag.appendChild(body.ownerDocument.createTextNode(text.slice(last)));
    node.parentNode?.replaceChild(frag, node);
  }
  return body.innerHTML;
};

const TABLE_STYLE = 'border-collapse: collapse; width: 100%; margin: 12px 0;';
const CELL_STYLE = 'border: 1px solid #999; padding: 4px 8px; vertical-align: top;';

/** Editor HTML → template HTML to store (sanitised, field tags back to {{field}}). */
export const fromEditorHtml = (editorHtml: string): string => {
  const clean = DOMPurify.sanitize(editorHtml);
  const body = parse(clean);

  body.querySelectorAll(`span[${FIELD_ATTR}]`).forEach(span => {
    const key = span.getAttribute(FIELD_ATTR) ?? '';
    span.replaceWith(body.ownerDocument.createTextNode(`{{${key}}}`));
  });

  // The editor wraps the text of every list item, table cell and box in a <p>;
  // the renderer then gives that <p> paragraph margins, spacing bullets and
  // meeting-schedule lines out. Unwrap a lone paragraph that has no styling.
  const unwrapLoneParagraph = (el: Element) => {
    const only = el.childNodes.length === 1 ? el.firstElementChild : null;
    if (only && only.tagName === 'P' && !only.hasAttribute('style')) {
      el.replaceChildren(...Array.from(only.childNodes));
    }
  };
  body.querySelectorAll('li, div').forEach(unwrapLoneParagraph);

  // Letters are printed via html2pdf, so tables need inline borders to show at all.
  body.querySelectorAll('table').forEach(table => {
    table.setAttribute('style', TABLE_STYLE);
    table.querySelectorAll('colgroup').forEach(cg => cg.remove());
  });
  body.querySelectorAll('td, th').forEach(cell => {
    cell.setAttribute('style', CELL_STYLE);
    unwrapLoneParagraph(cell);
  });

  const html = body.innerHTML;
  return html === '<p></p>' ? '' : html;
};
